import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
} from '@angular/core';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { getButtonList } from 'components/form-button/form-button.utils';
import { IFormButtonUi } from 'components/form-button/model';
import InputMask from 'inputmask';
import { of } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  skip,
  startWith,
} from 'rxjs/operators';

import { FormService } from 'common/dynamic-form/services/form.service';
import { HiddenService } from 'common/dynamic-form/services/hidden.service';
import { getFieldButtons } from 'common/dynamic-form/wrappers/field-base/buttons/buttons.utils';

import { ISPFieldType } from '../../model';
import { ISPFieldTypeBase } from '../field-type-base.class';

/**
 * `@type=otp` OTP field component
 *
 * Use only with Formly
 */
@UntilDestroy()
@Component({
  selector: 'isp-formly-otp-field',
  templateUrl: './otp.component.html',
  styleUrls: ['./scss/otp.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OTPFieldComponent
  extends ISPFieldTypeBase<ISPFieldType.OTP>
  implements OnInit, AfterViewInit
{
  private mask: InputMask.Instance;

  private timerValue: number;

  private intervalId?: number;

  private underfieldButtons: IFormButtonUi[];

  /** Input element reference */
  @ViewChild('inputRef')
  inputElement: ElementRef<HTMLInputElement>;

  constructor(
    private readonly formService: FormService,
    private readonly hiddenService: HiddenService,
  ) {
    super();
  }

  private hideTimer(): void {
    this.props.customFieldButtons.next(this.underfieldButtons);
    this.props.customFieldDescription.next({
      msg: '',
      position: 'under',
      hidden$: of(true),
      descItems: [],
    });
    clearInterval(this.intervalId);
    delete this.intervalId;
  }

  private updateTimer(): void {
    this.timerValue--;
    this.props.customFieldDescription.next({
      msg: '',
      hidden$: of(false),
      position: 'under',
      descItems: [
        {
          text: this.props.descMacros.replace(
            '__WAIT__',
            this.timerValue.toString(),
          ),
          hidden$: of(false),
        },
      ],
    });

    if (this.timerValue <= 0) {
      this.hideTimer();
    }
    // need to update desc
    this.cdr.markForCheck();
  }

  private startTimer(timeout: string): void {
    this.timerValue = parseInt(timeout, 10);
    if (isNaN(this.timerValue) || this.timerValue <= 0) {
      this.hideTimer();
      return;
    }

    this.props.customFieldButtons.next([]);
    this.props.customFieldDescription.next({
      msg: '',
      hidden$: of(false),
      position: 'under',
      descItems: [
        {
          text: this.props.descMacros.replace('__WAIT__', timeout),
          hidden$: of(false),
        },
      ],
    });

    this.intervalId = window.setInterval(() => {
      this.updateTimer();
    }, 1000);
  }

  /**
   * Set mask to input
   *
   * @param inputControl - input html element, on which mask will be applied
   * @param mask - mask value
   */
  private setMask(inputControl: HTMLInputElement, mask?: string): void {
    this.mask = InputMask({
      mask,
      showMaskOnHover: false,
      placeholder: '_',
    });
    this.mask.mask(inputControl);
  }

  private getUnderfieldButtons(): IFormButtonUi[] {
    return getButtonList(
      this.formService.loadedDoc,
      getFieldButtons(this.props.originalField) || [],
      false,
    );
  }

  private restartTimerOnTimerFieldChange(): void {
    this.form.controls[this.props.timeoutName].valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(timeoutValue => {
        if (this.intervalId) {
          clearInterval(this.intervalId);
          delete this.intervalId;
        }
        this.startTimer(timeoutValue);
      });
  }

  private toggleTimerOnHidden(): void {
    this.hiddenService
      .isHidden$(this.props.originalField.$name)
      .pipe(distinctUntilChanged(), untilDestroyed(this))
      .subscribe(isHidden => {
        if (isHidden) {
          clearInterval(this.intervalId);
          delete this.intervalId;
        }
        if (!this.intervalId && !isHidden) {
          this.startTimer(this.form.value[this.props.timeoutName]);
        }
      });
  }

  private emitButtonClickOnFullfill(): void {
    this.formControl.valueChanges
      .pipe(
        debounceTime(200),
        startWith(this.formControl.value),
        distinctUntilChanged(),
        skip(1),
        untilDestroyed(this),
      )
      .subscribe(() => {
        if (
          this.props.submitButton &&
          this.inputElement.nativeElement.inputmask?.isValid()
        ) {
          this.dynamicFormService.emitButtonClick(this.props.submitButton);
        }
      });
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.underfieldButtons = this.getUnderfieldButtons();

    this.restartTimerOnTimerFieldChange();
    this.toggleTimerOnHidden();
  }

  ngAfterViewInit(): void {
    this.setMask(this.inputElement.nativeElement, this.props.mask);

    this.emitButtonClickOnFullfill();
  }
}
